import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { aboutSections, aboutBadges } from '../mockData';
import { createFloatingAnimation, createStaggerContainer, fadeInLeft, fadeInUp, fadeInRight, getScrollAnimationProps, scaleIn } from '../lib/animations';
import SplitText from './ui/SplitText';
import TiltedCard from './ui/TiltedCard';
import ParallaxImage from './ui/ParallaxImage';
import sirianeImg from '../assets/images/siriane.jpg';

const AboutSection = () => {
  const shouldReduceMotion = useReducedMotion();
  const scrollAnimationProps = getScrollAnimationProps(shouldReduceMotion, { amount: 0.3 });
  const containerVariants = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.18, delayChildren: 0.1 });

  return (
    <motion.section id="a-propos" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-[#d4a594] via-[#e8c4b8] to-[#d4a594]" {...scrollAnimationProps} variants={containerVariants}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <motion.div className="order-2 lg:order-1" variants={createStaggerContainer({ staggerChildren: 0.16 })}>
            <motion.p className="text-xs sm:text-sm uppercase tracking-widest text-[#F5F0E8]/80 mb-3 sm:mb-4 font-medium" variants={fadeInUp}>
              Neuro Thérapie pour une approche globale du bien-être
            </motion.p>
            <SplitText
              text="Siriane Belliard-Reynes"
              delay={80}
              className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#F5F0E8] mb-2 sm:mb-3"
            />
            <motion.p className="text-base sm:text-lg text-[#F5F0E8]/90 mb-6 sm:mb-8 font-medium" variants={fadeInUp}>
              Neuro Thérapeute
            </motion.p>

            <motion.div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8" variants={createStaggerContainer({ staggerChildren: 0.14 })}>
              {aboutSections.map((section, index) => (
                <motion.div key={index} variants={fadeInLeft} className="relative pl-2">
                  <span className="absolute left-0 top-2 w-1 h-8 bg-[#F5F0E8]/60 rounded-full hidden sm:block" />
                  <h3 className="text-lg sm:text-xl font-serif text-[#F5F0E8] mb-2">{section.title}</h3>
                  <p className="text-sm sm:text-base text-[#F5F0E8]/80 leading-relaxed whitespace-pre-line">{section.text}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div className="flex flex-wrap gap-3" variants={createStaggerContainer({ staggerChildren: 0.1 })}>
              {aboutBadges.map((badge, index) => (
                <motion.span
                  key={index}
                  className="bg-white/20 backdrop-blur-sm text-[#F5F0E8] px-4 py-2 rounded-full text-xs sm:text-sm font-medium hover:bg-white/30 transition-colors"
                  variants={scaleIn}
                  whileHover={shouldReduceMotion ? undefined : { y: -3 }}
                >
                  {badge}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>

          <motion.div className="order-1 lg:order-2 relative" variants={fadeInRight}>
            <motion.div
              className="absolute -top-10 -right-6 w-32 h-32 rounded-full bg-[#F5F0E8]/20 blur-2xl z-0"
              animate={shouldReduceMotion ? undefined : createFloatingAnimation({ offset: 18, duration: 24 })}
            />
            <TiltedCard className="relative z-10 overflow-hidden rounded-2xl shadow-2xl aspect-[4/5] sm:aspect-[3/4]">
              <ParallaxImage
                src={sirianeImg}
                alt="Siriane Belliard-Reynes - Neuro Thérapeute"
                className="w-full h-full"
                speed={0.08}
              />
            </TiltedCard>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default AboutSection;